import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, Loader2, RotateCcw, X } from 'lucide-react';
import type { DocumentNode } from '../../types/documents';
import { getPresignedDocumentDownload } from '../../services/documents';
import { classifyDocumentPreview } from '../../utils/documentPreview';
import { Button } from '../ui/button';
import './documents-responsive.css';

interface Props {
  node: DocumentNode;
  canDownload: boolean;
  hasPrevious?: boolean;
  hasNext?: boolean;
  onClose: () => void;
  onDownload: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
}

export function DocumentReader({ node, canDownload, hasPrevious = false, hasNext = false, onClose, onDownload, onPrevious, onNext }: Props) {
  const kind = classifyDocumentPreview(node);
  const [url, setUrl] = useState<string | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [revision, setRevision] = useState(0);
  const [pdfFrameLoaded, setPdfFrameLoaded] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previous && previous !== document.body) previous.focus?.();
      else document.querySelector<HTMLElement>('[data-cubone-root]')?.focus();
    };
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    setUrl(null); setText(null); setError(false); setPdfFrameLoaded(false);
    if (kind === 'unsupported') return () => controller.abort();
    getPresignedDocumentDownload(node.id, 'inline').then(async ({ url: nextUrl }) => {
      if (controller.signal.aborted) return;
      setUrl(nextUrl);
      if (kind === 'text') {
        const response = await fetch(nextUrl, { signal: controller.signal });
        if (!response.ok) throw new Error('preview_fetch_failed');
        const value = await response.text();
        if (!controller.signal.aborted) setText(value.slice(0, 2_000_000));
      }
    }).catch(() => { if (!controller.signal.aborted) setError(true); });
    return () => controller.abort();
  }, [kind, node.id, revision]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') { event.preventDefault(); onClose(); return; }
    if (event.key === 'ArrowLeft' && hasPrevious && onPrevious) { event.preventDefault(); onPrevious(); return; }
    if (event.key === 'ArrowRight' && hasNext && onNext) { event.preventDefault(); onNext(); return; }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])') ?? []);
    if (focusable.length === 0) { event.preventDefault(); return; }
    const first = focusable[0]; const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  return <div ref={dialogRef} className="document-reader fixed inset-x-0 top-0 h-[100dvh] z-[70] flex bg-black/60 p-0 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="document-reader-title" onKeyDown={handleKeyDown}>
    <div className="m-auto flex h-full w-full max-w-6xl flex-col overflow-hidden bg-[var(--hexa-surface)] sm:h-[min(92dvh,100%)] sm:rounded-xl">
      <header className="grid shrink-0 grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-2 border-b p-2 sm:gap-3 sm:p-3 md:grid-cols-[auto_minmax(0,1fr)_auto_auto]">
        <div className="col-start-1 row-start-1 flex shrink-0 gap-1"><Button size="icon" variant="ghost" disabled={!hasPrevious} onClick={onPrevious} aria-label="Documento precedente"><ChevronLeft className="h-4 w-4" /></Button><Button size="icon" variant="ghost" disabled={!hasNext} onClick={onNext} aria-label="Documento successivo"><ChevronRight className="h-4 w-4" /></Button></div>
        <h2 id="document-reader-title" className="col-span-3 col-start-1 row-start-2 max-h-[20dvh] min-w-0 overflow-y-auto break-words [overflow-wrap:anywhere] font-semibold md:col-span-1 md:col-start-2 md:row-start-1">{node.name}</h2>
        {canDownload && <Button className="col-start-2 row-start-1 md:col-start-3" size="sm" variant="outline" onClick={onDownload} aria-label="Scarica"><Download className="h-4 w-4 mr-1" />Scarica</Button>}
        <button ref={closeRef} className="col-start-3 row-start-1 flex h-10 w-10 items-center justify-center rounded p-1 md:col-start-4" aria-label="Chiudi anteprima" onClick={onClose}><X /></button>
      </header>
      <main className="min-h-0 flex-1 overflow-auto bg-neutral-100 p-2 sm:p-3 dark:bg-neutral-950">
        {kind === 'unsupported' ? <ReaderState title="Anteprima non disponibile per questo formato." action={canDownload ? <Button onClick={onDownload}>Scarica</Button> : undefined} />
          : error ? <ReaderState title="Impossibile caricare l’anteprima o il collegamento è scaduto." action={<Button onClick={() => setRevision((value) => value + 1)}><RotateCcw className="mr-1 h-4 w-4" />Riprova</Button>} />
          : kind === 'text' && text !== null ? <pre className="mx-auto min-h-full max-w-5xl whitespace-pre-wrap break-words rounded bg-white p-3 text-sm text-black sm:p-4">{text || 'Documento vuoto'}</pre>
          : kind === 'image' && url ? <div className="grid min-h-full place-items-center overflow-auto"><img src={url} alt={node.name} className="max-h-full max-w-full object-contain" /></div>
          : kind === 'pdf' && url ? <div className="relative h-full min-h-0">
            <iframe title={`Anteprima ${node.name}`} src={url} onLoad={() => setPdfFrameLoaded(true)} className="h-full w-full bg-white" />
            <div data-testid="pdf-preview-loading-overlay" aria-hidden={pdfFrameLoaded} className={`absolute inset-0 grid place-content-center justify-items-center gap-3 bg-white text-center transition-opacity duration-200 dark:bg-neutral-950 ${pdfFrameLoaded ? 'pointer-events-none opacity-0' : 'opacity-100'}`}>
              <Loader2 className="animate-spin" />
              <p>Caricamento anteprima...</p>
            </div>
          </div>
          : <ReaderState title="Caricamento anteprima..." icon={<Loader2 className="animate-spin" />} />}
      </main>
    </div>
  </div>;
}

function ReaderState({ title, icon, action }: { title: string; icon?: React.ReactNode; action?: React.ReactNode }) {
  return <div className="grid min-h-full place-content-center justify-items-center gap-3 text-center">{icon}<p>{title}</p>{action}</div>;
}
